import { create } from 'zustand';

export type Theme = 'system' | 'light' | 'dark';
export type ToastKind = 'info' | 'success' | 'warning' | 'error';
export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
  durationMs?: number;
};

function readLS<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}
export function writeLS(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

type UiState = {
  theme: Theme;
  sidebarCollapsed: boolean;
  sidebarDrawerOpen: boolean;
  paletteOpen: boolean;
  shortcutsOpen: boolean;
  toasts: Toast[];
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
  setSidebarDrawer: (open: boolean) => void;
  setPaletteOpen: (open: boolean) => void;
  setShortcutsOpen: (open: boolean) => void;
  pushToast: (t: Omit<Toast, 'id'>) => string;
  updateToast: (id: string, patch: Partial<Omit<Toast, 'id'>>) => void;
  dismissToast: (id: string) => void;
};

export const useUiStore = create<UiState>((set) => ({
  theme: readLS<Theme>('theme', 'system'),
  sidebarCollapsed: readLS('sidebarCollapsed', false),
  sidebarDrawerOpen: false,
  paletteOpen: false,
  shortcutsOpen: false,
  toasts: [],
  setTheme: (theme) => {
    writeLS('theme', theme);
    set({ theme });
  },
  toggleSidebar: () =>
    set((s) => {
      writeLS('sidebarCollapsed', !s.sidebarCollapsed);
      return { sidebarCollapsed: !s.sidebarCollapsed };
    }),
  setSidebarDrawer: (sidebarDrawerOpen) => set({ sidebarDrawerOpen }),
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
  setShortcutsOpen: (shortcutsOpen) => set({ shortcutsOpen }),
  pushToast: (t) => {
    const id = crypto.randomUUID();
    set((s) => ({ toasts: [...s.toasts.slice(-4), { ...t, id }] }));
    return id;
  },
  updateToast: (id, patch) => set((s) => ({ toasts: s.toasts.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(t: Omit<Toast, 'id'>): string {
  return useUiStore.getState().pushToast(t);
}
export function updateToast(id: string, patch: Partial<Omit<Toast, 'id'>>): void {
  useUiStore.getState().updateToast(id, patch);
}
export function dismissToast(id: string): void {
  useUiStore.getState().dismissToast(id);
}

/** Applies the theme class to <html>; called once from main.tsx. */
export function installTheme(): void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const apply = () => {
    const theme = useUiStore.getState().theme;
    const dark = theme === 'dark' || (theme === 'system' && mq.matches);
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  };
  apply();
  mq.addEventListener('change', apply);
  useUiStore.subscribe((s, prev) => {
    if (s.theme !== prev.theme) apply();
  });
}
